import React, { useEffect, useMemo, useState } from 'react';
import { Check, CircleNotch } from '@phosphor-icons/react';
import Modal from '../Settings/components/Modal';
import type { IntegrationResource } from '@common/api/brandGroupService';

type Props = {
  isOpen: boolean;
  provider: string;
  resources: IntegrationResource[];
  isLoading?: boolean;
  isSaving?: boolean;
  title?: string;
  description?: string;
  cancelLabel?: string;
  saveLabel?: string;
  onCancel: () => void;
  onSave: (resources: IntegrationResource[]) => void | Promise<void>;
};

export default function OnboardingResourceSelectionModal({
  isOpen,
  provider,
  resources,
  isLoading = false,
  isSaving = false,
  title,
  description = '请选择要加入默认业务组的二级资源。不选择不会自动授权任何二级资源，后续可在业务组中分配。',
  cancelLabel = '稍后选择',
  saveLabel = '保存选择',
  onCancel,
  onSave,
}: Props) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const hasResources = resources.length > 0;
  const resourceKeys = useMemo(
    () => new Set(resources.map((resource) => `${resource.resourceType}:${resource.resourceKey}`)),
    [resources],
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedKeys(new Set(resourceKeys));
  }, [isOpen, resourceKeys]);

  const toggle = (key: string) => {
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedResources = resources.filter((resource) =>
    selectedKeys.has(`${resource.resourceType}:${resource.resourceKey}`),
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSaving ? () => undefined : onCancel}
      title={title || `选择 ${provider} 资源`}
      width="max-w-2xl"
      bodyClassName="px-6 pb-6 pt-1"
    >
      <div className="space-y-4">
        <p className="text-sm leading-6 text-[#667085]">
          {description}
        </p>
        <div className="max-h-[min(420px,55vh)] overflow-y-auto rounded-xl border border-[#E8EBF3]">
          {isLoading ? (
            <div className="flex min-h-[240px] items-center justify-center px-5 py-12 text-center text-sm text-[#667085]">
              <CircleNotch size={18} className="mr-2 animate-spin" />
              正在加载可用资源...
            </div>
          ) : resources.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-[#667085]">
              暂未发现可用的二级资源。
            </div>
          ) : resources.map((resource) => {
            const key = `${resource.resourceType}:${resource.resourceKey}`;
            const selected = selectedKeys.has(key);
            return (
              <button
                key={key}
                type="button"
                disabled={isSaving}
                onClick={() => toggle(key)}
                className={`flex w-full items-center gap-3 border-b border-[#EEF1F6] px-4 py-3 text-left last:border-b-0 ${selected ? 'bg-[#F8F9FC]' : 'bg-white hover:bg-[#FCFCFD]'}`}
              >
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? 'border-[#1A162F] bg-[#1A162F] text-white' : 'border-[#C8D0E2] bg-white'}`}>
                  {selected ? <Check size={14} weight="bold" /> : null}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-[#1A162F]">
                    {resource.name || resource.resourceKey}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-[#667085]">
                    {resource.resourceType} · {resource.resourceKey}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <span className="text-sm text-[#667085]">已选择 {selectedResources.length} 项</span>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="rounded-[10px] px-4 py-2.5 text-sm font-medium text-[#667085] hover:bg-[#F8F9FC] disabled:opacity-50"
            >
              {hasResources ? cancelLabel : '关闭'}
            </button>
            {hasResources ? (
              <button type="button" onClick={() => void onSave(selectedResources)} disabled={isSaving || isLoading} className="inline-flex items-center gap-2 rounded-[10px] bg-[#1A162F] px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50">
                {isSaving ? <CircleNotch size={14} className="animate-spin" /> : null}
                {saveLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
